import React, { useRef } from "react";
import { Box, Button, Typography } from "@mui/material";
import { CloudUpload as UploadIcon } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { theme } from "../../../../theme";

interface ImageUploadDropZoneProps {
    onFilesSelected: (files: FileList) => void;
    dragActive: boolean;
    onDragOver: (event: React.DragEvent<HTMLDivElement>) => void;
    onDragLeave: (event: React.DragEvent<HTMLDivElement>) => void;
    onDrop: (event: React.DragEvent<HTMLDivElement>) => void;
}

export const ImageUploadDropZone: React.FC<ImageUploadDropZoneProps> = ({
    onFilesSelected,
    dragActive,
    onDragOver,
    onDragLeave,
    onDrop
}) => {
    const fileInputRef = useRef<HTMLInputElement>(null);
    const { t } = useTranslation();

    const handleFileInput = (event: React.ChangeEvent<HTMLInputElement>) => {
        const files = event.target.files;
        if (files) {
            onFilesSelected(files);
        }
        // Clear the input
        if (fileInputRef.current) {
            fileInputRef.current.value = "";
        }
    };

    const handleClick = () => {
        fileInputRef.current?.click();
    };

    return (
        <>
            <input
                type="file"
                multiple
                accept="image/*"
                style={{ display: "none" }}
                ref={fileInputRef}
                onChange={handleFileInput}
            />

            <Box
                sx={{
                    border: `1px dashed ${dragActive ? theme.palette.brand.main : "#ccc"}`,
                    borderRadius: 1,
                    p: 3,
                    mb: 3,
                    textAlign: "center",
                    bgcolor: dragActive ? "rgba(235, 48, 79, 0.04)" : "rgba(248, 249, 250, 0.8)",
                    cursor: "pointer",
                    transition: "all 0.2s ease",
                    "&:hover": {
                        bgcolor: "rgba(235, 48, 79, 0.04)",
                        borderColor: theme.palette.brand.main
                    }
                }}
                onDrop={onDrop}
                onDragOver={onDragOver}
                onDragLeave={onDragLeave}
                onClick={handleClick}
            >
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 2 }}>
                    <UploadIcon
                        sx={{
                            fontSize: 28,
                            color: dragActive ? theme.palette.brand.main : "#666",
                            transition: "color 0.2s ease"
                        }}
                    />
                    <Box sx={{ textAlign: "left" }}>
                        <Typography variant="body1" sx={{ fontWeight: 500, color: dragActive ? theme.palette.brand.main : "inherit", mb: 0.5 }}>
                            {dragActive ? t("complaints.images.dropImagesHere") : t("complaints.images.dropOrBrowse")}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            {t("complaints.images.maxFileSize")}
                        </Typography>
                    </Box>
                    <Button
                        variant="outlined"
                        size="small"
                        sx={{
                            borderColor: theme.palette.brand.main,
                            color: theme.palette.brand.main,
                            fontSize: "0.75rem",
                            "&:hover": {
                                borderColor: "#b91c1c",
                                bgcolor: "rgba(235, 48, 79, 0.04)"
                            }
                        }}
                        onClick={(e) => {
                            e.stopPropagation();
                            fileInputRef.current?.click();
                        }}
                    >
                        {t("complaints.images.browse")}
                    </Button>
                </Box>
            </Box>
        </>
    );
}; 